import React, { useMemo, useState } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtUSD, fmtECU } from "../lib/format";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Archive } from "lucide-react";
import { fmtDateTime, dubaiDayKey, DaySeparator } from "../lib/time";
import { PageErrorBoundary } from "../components/PageErrorBoundary";

const PAGE = 50;

function FeedInner() {
  const [agent, setAgent] = useState("all");

  const { data: alist } = useQuery({
    queryKey: ["agents-registry"],
    queryFn: async () => (await api.get("/settings/agents")).data,
  });

  // One hook, correctly handles paginated appends without any Rules-of-Hooks
  // trap. First page uses the backend's 48h default; subsequent pages pass
  // `before` = older timestamp from the previous page's tail.
  const {
    data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch,
  } = useInfiniteQuery({
    queryKey: ["feed", agent],
    queryFn: async ({ pageParam }) => {
      const params = { limit: PAGE };
      if (agent !== "all") params.agent = agent;
      if (pageParam) params.before = pageParam;
      return (await api.get("/dashboard/feed", { params })).data;
    },
    initialPageParam: null,
    getNextPageParam: (last) => {
      // Server tells us if there is more to fetch.
      if (!last?.has_more_events && !last?.has_more_runs) return undefined;
      const tails = [
        last?.events?.at(-1)?.created_at,
        last?.runs?.at(-1)?.created_at,
      ].filter(Boolean).sort(); // ascending — pick the older
      return tails[0] || undefined;
    },
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
    staleTime: 5000,
  });

  const pages = data?.pages || [];
  const events = useMemo(() => {
    const seen = new Set(); const out = [];
    for (const p of pages) {
      for (const e of (p?.events || [])) {
        if (!e || !e.id || seen.has(e.id)) continue;
        seen.add(e.id); out.push(e);
      }
    }
    return out;
  }, [pages]);
  const runs = useMemo(() => {
    const seen = new Set(); const out = [];
    for (const p of pages) {
      for (const r of (p?.runs || [])) {
        if (!r || !r.id || seen.has(r.id)) continue;
        seen.add(r.id); out.push(r);
      }
    }
    return out;
  }, [pages]);

  const groupedEvents = useMemo(() => {
    const out = []; let lastKey = null;
    for (const e of events) {
      const k = dubaiDayKey(e.created_at);
      if (k !== lastKey) { out.push({ type: "sep", key: `sep-${k}-${out.length}`, dayKey: k }); lastKey = k; }
      out.push({ type: "evt", key: `e-${e.id}`, evt: e });
    }
    return out;
  }, [events]);
  const groupedRuns = useMemo(() => {
    const out = []; let lastKey = null;
    for (const r of runs) {
      const k = dubaiDayKey(r.created_at);
      if (k !== lastKey) { out.push({ type: "sep", key: `rsep-${k}-${out.length}`, dayKey: k }); lastKey = k; }
      out.push({ type: "run", key: `r-${r.id}`, run: r });
    }
    return out;
  }, [runs]);

  const changeAgent = (v) => { setAgent(v); refetch(); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-5 h-screen overflow-hidden">
      <div className="flex items-end justify-between flex-wrap gap-3 shrink-0">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Agent feed</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">Last 48 hours · polling live</p>
        </div>
        <Select value={agent} onValueChange={changeAgent}>
          <SelectTrigger className="w-48" data-testid="feed-agent-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All agents</SelectItem>
            {(alist?.agents || []).map((a) => (
              <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        className="flex items-start gap-2 rounded-md bg-card border border-border px-3 py-2 shrink-0"
        data-testid="feed-archive-note"
      >
        <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
        <p className="font-mono text-[10.5px] text-muted-foreground leading-snug">
          Showing the last 48 hours by default. Older activity is archived — use "Load more" to page back 50 at a time.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
        <div className="border border-border rounded-xl bg-card p-5 flex flex-col min-h-0">
          <h2 className="font-display text-lg font-semibold mb-3 shrink-0">Events</h2>
          <div className="flex-1 min-h-0 overflow-y-auto -mx-2 px-2" data-testid="feed-events-scroll">
            {events.length === 0 ? (
              <p className="text-sm text-muted-foreground">No events in the last 48 hours.</p>
            ) : (
              <ul className="space-y-2.5" data-testid="feed-events">
                {groupedEvents.map((item) =>
                  item.type === "sep" ? (
                    <li key={item.key}><DaySeparator dayKey={item.dayKey} /></li>
                  ) : (
                    <li key={item.key} className="text-sm" data-testid={`feed-event-${item.evt.id}`}>
                      <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
                        <span className="uppercase">{item.evt.event_type || "event"}</span>
                        <span>·</span>
                        <span>{item.evt.actor_name || "—"}</span>
                        <span>·</span>
                        <span>{fmtDateTime(item.evt.created_at)}</span>
                      </div>
                      {item.evt.message && <p className="mt-0.5 whitespace-pre-wrap break-words">{item.evt.message}</p>}
                    </li>
                  )
                )}
              </ul>
            )}
          </div>
        </div>

        <div className="border border-border rounded-xl bg-card p-5 flex flex-col min-h-0">
          <h2 className="font-display text-lg font-semibold mb-3 shrink-0">Agent runs</h2>
          <div className="flex-1 min-h-0 overflow-y-auto -mx-2 px-2" data-testid="feed-runs-scroll">
            {runs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No runs recorded in the last 48 hours.</p>
            ) : (
              <ul className="space-y-2.5" data-testid="feed-runs">
                {groupedRuns.map((item) =>
                  item.type === "sep" ? (
                    <li key={item.key}><DaySeparator dayKey={item.dayKey} /></li>
                  ) : (
                    <li key={item.key} className="text-sm">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
                          <span>{item.run.agent_name || "—"}</span>
                          <span>·</span>
                          <span className={item.run.status === "success" ? "text-emerald-500" : "text-rose-500"}>
                            {item.run.status || "—"}
                          </span>
                          <span>·</span>
                          <span>{fmtDateTime(item.run.created_at)}</span>
                        </div>
                        <div className="font-mono text-xs">
                          <span style={{color: "hsl(var(--usd))"}}>{fmtUSD(item.run.claude_cost_usd)}</span>
                          <span className="mx-1">·</span>
                          <span style={{color: "hsl(var(--ecu))"}}>{fmtECU(item.run.emergent_ecu)}</span>
                        </div>
                      </div>
                      {item.run.summary && <p className="mt-0.5 whitespace-pre-wrap break-words">{item.run.summary}</p>}
                    </li>
                  )
                )}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-center shrink-0">
        {hasNextPage && (
          <Button
            variant="outline"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            data-testid="feed-load-more"
          >
            {isFetchingNextPage ? "Loading…" : "Load 50 more"}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Feed() {
  return (
    <PageErrorBoundary>
      <FeedInner />
    </PageErrorBoundary>
  );
}
